import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowDownIcon, ArrowRightIcon, CreditCardIcon, DatabaseIcon, FileTextIcon, HomeIcon, InfinityIcon, MessageCircleIcon, PieChartIcon, ShuffleIcon, TruckIcon, UserIcon, UsersIcon, WalletIcon, BoxIcon } from "lucide-react";
import { useLanguage } from "../LanguageContext";
import { BROSSARD_IMAGES, transformCopy as c, Bi } from "../../data/brossardCase";
type Pick = (b: Bi) => string;
const EASE = [0.23, 1, 0.32, 1] as const;
const reveal = {
  initial: {
    opacity: 0,
    y: 12
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true,
    margin: '-80px'
  },
  transition: {
    duration: 0.3,
    ease: EASE
  }
};
const BEFORE_ICONS: Record<(typeof c.before.items)[number]['key'], BoxIcon> = {
  separate: DatabaseIcon,
  identities: UsersIcon,
  fragmented: ShuffleIcon,
  manual: FileTextIcon
};
const SERVICE_ICONS: Record<(typeof c.core.services)[number]['key'], BoxIcon> = {
  card: CreditCardIcon,
  comms: MessageCircleIcon,
  collect: TruckIcon,
  requests: FileTextIcon,
  payments: WalletIcon,
  municipal: HomeIcon
};
const TODAY_ICONS: Record<(typeof c.today.items)[number]['key'], BoxIcon> = {
  identities: UserIcon,
  coverage: PieChartIcon,
  relation: InfinityIcon
};
/* Service positions around the identity (percent of the orbit box) */
const POSITIONS: Record<(typeof c.core.services)[number]['key'], {
  x: number;
  y: number;
}> = {
  card: {
    x: 22,
    y: 14
  },
  comms: {
    x: 78,
    y: 14
  },
  collect: {
    x: 12,
    y: 52
  },
  requests: {
    x: 88,
    y: 52
  },
  payments: {
    x: 22,
    y: 88
  },
  municipal: {
    x: 78,
    y: 88
  }
};
const PANEL = 'rounded-2xl border border-[#ECEBF5] bg-white';
function FlowArrow() {
  return <>
      <span className="hidden xl:flex items-center justify-center" aria-hidden="true" data-id="element-1348" data-mp="3tmu1tm2a1agz">
        <ArrowRightIcon className="w-4 h-4 text-[#6D66EE]" strokeWidth={1.6} data-id="element-1349" data-mp="1i8afe8xh0lc" />
      </span>
      <span className="xl:hidden flex justify-center py-1" aria-hidden="true" data-id="element-1350" data-mp="2455ltlwabrmf">
        <ArrowDownIcon className="w-4 h-4 text-[#6D66EE]" strokeWidth={1.6} data-id="element-1351" data-mp="1zdxcepuyg699" />
      </span>
    </>;
}
function BeforePanel({
  pick
}: {
  pick: Pick;
}) {
  return <div className={`${PANEL} p-5 bg-[#FCFCFD]`} data-id="element-1352" data-mp="1ufdleb1i84a1">
      <h3 className="font-sans text-[12px] font-semibold tracking-[0.2em] uppercase text-[#121A3A]" data-id="element-1353" data-mp="1pg12209udfc7">{pick(c.before.title)}</h3>
      <ul className="mt-5 space-y-5" data-id="element-1354" data-mp="2n2gkb4a3eh0t">
        {c.before.items.map(it => {
        const Icon = BEFORE_ICONS[it.key];
        return <li key={it.key} className="flex items-start gap-3" data-id="element-1355" data-mp="2i57ymtpqjuut">
              <Icon className="w-4 h-4 mt-0.5 text-[#9AA0B4] shrink-0" strokeWidth={1.7} aria-hidden="true" data-id="element-1356" data-mp="2d4k96ch5nm2p" />
              <span className="font-sans text-[13px] text-[#6B7288] leading-snug" data-id="element-1357" data-mp="292p0gmwwa6ew">
                {pick(it.label)}
                {it.note && <span className="block text-[11px] text-[#9AA0B4]" data-id="element-1358" data-mp="11rhpv9no8ieu">{pick(it.note)}</span>}
              </span>
            </li>;
      })}
      </ul>
    </div>;
}
function IdentityCircle({
  pick
}: {
  pick: Pick;
}) {
  return <div className="w-[150px] h-[150px] rounded-full bg-[#F6F5FE] ring-1 ring-[#E4E1FB] shadow-[0_0_0_14px_rgba(241,240,253,0.6)] flex flex-col items-center justify-center text-center px-3" data-id="element-1359" data-mp="39a3tezkpxo27">
      <UserIcon className="w-6 h-6 text-[#4F46E5]" strokeWidth={1.7} aria-hidden="true" data-id="element-1360" data-mp="3iaijec8mnrc1" />
      <p className="mt-2 font-sans text-[12px] font-bold tracking-[0.08em] uppercase text-[#121A3A] leading-tight" data-id="element-1361" data-mp="3n5eks2zr7r1t">
        {c.core.identity.map(w => <span key={w.fr} className="block" data-id="element-1362" data-mp="vlq1b5q2wox">{pick(w)}</span>)}
      </p>
      <p className="mt-1 font-sans text-[9.5px] text-[#6B7288] leading-snug" data-id="element-1363" data-mp="xy8rqjb1b31">{pick(c.core.who)}</p>
    </div>;
}
function CorePanel({
  pick
}: {
  pick: Pick;
}) {
  return <div className={`${PANEL} p-5`} data-id="element-1364" data-mp="9w9vhhh1voq7">
      <h3 className="text-center font-sans text-[12px] font-semibold tracking-[0.2em] uppercase text-[#121A3A]" data-id="element-1365" data-mp="26p3axw57u2p5">{c.core.title}</h3>

      {/* Orbit — tablet & desktop */}
      <div className="relative hidden sm:block mt-3 h-[230px]" data-id="element-1366" data-mp="2boesv8hbje8b">
        <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true" data-id="element-1367" data-mp="1gcyjax395888">
          {Object.values(POSITIONS).map((p, i) => <line key={i} x1="50" y1="52" x2={p.x} y2={p.y} stroke="#D6D3F8" strokeWidth="0.5" vectorEffect="non-scaling-stroke" data-id="element-1368" data-mp="1qc5zds1dfgz1" data-mpi={i} />)}
        </svg>
        <div className="absolute left-1/2 top-[52%] -translate-x-1/2 -translate-y-1/2" data-id="element-1369" data-mp="1lee5wtl2vmsq">
          <IdentityCircle pick={pick} data-id="element-1370" data-mp="xoju8fchw4a2" />
        </div>
        {c.core.services.map(s => {
        const Icon = SERVICE_ICONS[s.key];
        const p = POSITIONS[s.key];
        return <div key={s.key} className="absolute -translate-x-1/2 -translate-y-1/2 flex flex-col items-center gap-1 bg-white px-1" style={{
          left: `${p.x}%`,
          top: `${p.y}%`
        }} data-id="element-1371" data-mp="17d1sopxaxzaj">
              <span className="w-8 h-8 rounded-lg bg-white border border-[#E7E6F5] flex items-center justify-center" data-id="element-1372" data-mp="ewh6yl5clpqu">
                <Icon className="w-4 h-4 text-[#4F46E5]" strokeWidth={1.7} aria-hidden="true" data-id="element-1373" data-mp="9wuxmmsn4tnb" />
              </span>
              <span className="font-sans text-[11.5px] text-[#3A4260] whitespace-nowrap" data-id="element-1374" data-mp="2ljonk38ob35q">{pick(s.label)}</span>
            </div>;
      })}
      </div>

      {/* Stacked — mobile */}
      <div className="sm:hidden mt-5 flex flex-col items-center" data-id="element-1375" data-mp="2bo4t7l9rccj3">
        <IdentityCircle pick={pick} data-id="element-1376" data-mp="2gm6duamu0jwo" />
        <span className="w-px h-5 bg-[#D6D3F8] mt-4" aria-hidden="true" data-id="element-1377" data-mp="2gn7wb066g8of" />
        <ul className="grid grid-cols-2 gap-2 w-full" data-id="element-1378" data-mp="34b93o5rmdj29">
          {c.core.services.map(s => {
          const Icon = SERVICE_ICONS[s.key];
          return <li key={s.key} className="flex items-center gap-2 rounded-lg border border-[#ECEBF5] px-3 py-2" data-id="element-1379" data-mp="39a2hjzatjc2y">
                <Icon className="w-4 h-4 text-[#4F46E5] shrink-0" strokeWidth={1.7} aria-hidden="true" data-id="element-1380" data-mp="2vd5f10lc8668" />
                <span className="font-sans text-[12px] text-[#3A4260]" data-id="element-1381" data-mp="2zeinliftengu">{pick(s.label)}</span>
              </li>;
        })}
        </ul>
      </div>
    </div>;
}
function TodayPanel({
  pick
}: {
  pick: Pick;
}) {
  return <div className={`${PANEL} p-5`} data-id="element-1382" data-mp="3qk6csvsns8cx">
      <h3 className="font-sans text-[12px] font-semibold tracking-[0.2em] uppercase text-[#121A3A]" data-id="element-1383" data-mp="3qj4ovqqjnrds">{pick(c.today.title)}</h3>
      <ul className="mt-5 space-y-5" data-id="element-1384" data-mp="3llv4ipi0i5wt">
        {c.today.items.map(it => {
        const Icon = TODAY_ICONS[it.key];
        const text = it.key === 'relation';
        return <li key={it.key} className="flex items-start gap-3" data-id="element-1385" data-mp="3bw1q7gqhjcov">
              <span className="w-8 h-8 rounded-full bg-[#F1F0FD] flex items-center justify-center shrink-0" data-id="element-1386" data-mp="37topj6qli4yy">
                <Icon className="w-4 h-4 text-[#4F46E5]" strokeWidth={1.8} aria-hidden="true" data-id="element-1387" data-mp="32uv8j4c3nvu1" />
              </span>
              <span className="min-w-0" data-id="element-1388" data-mp="2xupe5geg3v89">
                <span className={`block font-sans font-semibold text-[#121A3A] leading-tight ${text ? 'text-[13px]' : 'text-[1.2rem] whitespace-nowrap'}`} data-id="element-1389" data-mp="2xvr0ih0ww45a">
                  {pick(it.value)}
                </span>
                <span className={`block font-sans text-[#4A5268] ${text ? 'text-[13px] font-semibold text-[#121A3A]' : 'text-[12.5px]'}`} data-id="element-1390" data-mp="w1rrk2b9hyvr">{pick(it.label)}</span>
                <span className="block font-sans text-[11px] text-[#8A8FA6]" data-id="element-1391" data-mp="zla1l3g6e94o">{pick(it.note)}</span>
              </span>
            </li>;
      })}
      </ul>
    </div>;
}
function ImpactPanel({
  pick
}: {
  pick: Pick;
}) {
  return <div className="rounded-2xl border border-[#E2E0FA] bg-[#F6F5FE] p-5 flex flex-col" data-id="element-1392" data-mp="18f56caddxxjs">
      <div className="flex items-center gap-4" data-id="element-1393" data-mp="1dcyakxv4ohhf">
        <span className="w-10 h-10 rounded-xl bg-white border border-[#E2E0FA] flex items-center justify-center shrink-0" data-id="element-1394" data-mp="gnh26b4rghc1">
          <UsersIcon className="w-5 h-5 text-[#4F46E5]" strokeWidth={1.7} aria-hidden="true" data-id="element-1395" data-mp="kpu4epkatx42" />
        </span>
        <p className="font-sans text-[2.4rem] font-semibold leading-none text-[#121A3A] whitespace-nowrap" data-id="element-1396" data-mp="pn42etlr7h3i">
          {c.impact.from} <span className="text-[#4F46E5]" data-id="element-1397" data-mp="uo9tkmzrmwax">→ {c.impact.to}</span>
        </p>
      </div>
      <p className="mt-4 font-sans text-[13px] text-[#4A5268] leading-relaxed" data-id="element-1398" data-mp="212ivvner7qtv">{pick(c.impact.text)}</p>
      <img src={BROSSARD_IMAGES.counter} alt={pick({
      fr: 'Comptoir de service municipal presque sans attente',
      en: 'Municipal service counter with almost no queue'
    })} className="mt-auto pt-4 w-full h-[120px] object-cover rounded-xl" data-id="element-1399" data-mp="260apclv1rl06" />
    </div>;
}
export function BrossardTransformation() {
  const {
    lang
  } = useLanguage();
  const pick: Pick = b => lang === 'FR' ? b.fr : b.en;
  return <section id="brossard-transformation" className="relative bg-white border-t border-[#EEF0F4] px-6 py-14 lg:py-16 scroll-mt-24" aria-labelledby="brossard-transform-title" data-id="element-1400" data-mp="2w368mutqmlau">
      <div className="max-w-[1320px] mx-auto" data-id="element-1401" data-mp="35uanlujey65o">
        <div className="grid grid-cols-1 xl:grid-cols-[280px_1fr] gap-8 xl:gap-6 items-start" data-id="element-1402" data-mp="3juv4gl5rprwj">
          <motion.div {...reveal} data-id="element-1403" data-mp="3evqye3e54aaf">
            <p className="font-sans text-[11.5px] font-semibold tracking-[0.2em] uppercase text-[#4F46E5]" data-id="element-1404" data-mp="3ewtoi93n28pa">{pick(c.eyebrow)}</p>
            <h2 id="brossard-transform-title" className="mt-4 font-serif text-[1.9rem] sm:text-[2.2rem] leading-[1.06] tracking-tight" data-id="element-1405" data-mp="1phcatmeuytgi">
              {c.headline.map(l => <span key={l.text.fr} className={`block ${l.accent ? 'text-[#4F46E5]' : 'text-[#121A3A]'}`} data-id="element-1406" data-mp="3w4kwqe0v64fc">{pick(l.text)}</span>)}
            </h2>
          </motion.div>

          <motion.div {...reveal} className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-[0.9fr_auto_1.35fr_auto_0.95fr_1.1fr] gap-3 xl:gap-2 items-stretch" data-id="element-1407" data-mp="50me0w5ixg1l">
            <BeforePanel pick={pick} data-id="element-1408" data-mp="3rd1nq8orzs6q" />
            <span className="hidden xl:flex" data-id="element-1409" data-mp="3rc05nk9ce5fi"><FlowArrow data-id="element-1410" data-mp="3debtd0tcc6m2" /></span>
            <span className="md:hidden" data-id="element-1411" data-mp="3dd0fg866lih8"><FlowArrow data-id="element-1412" data-mp="3dgy1b1k58o4t" /></span>
            <CorePanel pick={pick} data-id="element-1413" data-mp="3ie7n878bqhnw" />
            <span className="hidden xl:flex" data-id="element-1414" data-mp="3if9arb6j0wmi"><FlowArrow data-id="element-1415" data-mp="3ibbq2l4avx0i" /></span>
            <span className="md:hidden" data-id="element-1416" data-mp="34ef2qtne4xod"><FlowArrow data-id="element-1417" data-mp="34f875nyvnb3c" /></span>
            <TodayPanel pick={pick} data-id="element-1418" data-mp="34g9lq2f3qfpt" />
            <span className="md:hidden" data-id="element-1419" data-mp="38eyybssbft8e"><FlowArrow data-id="element-1420" data-mp="38ga05n27aqwv" /></span>
            <ImpactPanel pick={pick} data-id="element-1421" data-mp="38hbnapwhr3uy" />
          </motion.div>
        </div>

        <motion.div {...reveal} className="mt-8 rounded-2xl border border-[#ECEBF7] bg-[#FAFAFE] px-6 md:px-10 py-6 flex flex-col lg:flex-row lg:items-center gap-6" data-id="element-1422" data-mp="12n1v3wu1gbom">
          <UsersIcon className="w-10 h-10 text-[#4F46E5] shrink-0" strokeWidth={1.4} aria-hidden="true" data-id="element-1423" data-mp="3f6ak6ahfj4d5" />
          <p className="flex-1 font-serif text-[1.15rem] md:text-[1.3rem] leading-snug text-[#121A3A]" data-id="element-1424" data-mp="3jyc5sn0hm7fu">
            {pick(c.statement.line1)}
            <br className="hidden md:block" data-id="element-1425" data-mp="356beclce9s3z" /> {pick(c.statement.before)}
            <span className="text-[#4F46E5]" data-id="element-1426" data-mp="3a890pog3d315">{pick(c.statement.accent)}</span>
            {pick(c.statement.after)}
          </p>
          <Link to="/platform" className="lg:pl-8 lg:border-l lg:border-[#E2E0FA] inline-flex items-center gap-2 font-sans text-[14px] font-medium text-[#4F46E5] hover:text-[#4338CA] whitespace-nowrap transition-colors duration-150" data-id="element-1427" data-mp="1pl5lypfh0yl">
            {pick(c.statement.link)}
            <ArrowRightIcon className="w-4 h-4" aria-hidden="true" data-id="element-1428" data-mp="3swe0ylxji5v7" />
          </Link>
        </motion.div>
      </div>
    </section>;
}